import React from "react";
import Link from "next/link";
import { BreadcrumbJsonLd } from "next-seo";
import MetaTags from "@/components/seo/meta-tags";
import SitemapLinkList from "@/components/Common/SitemapLinkList";
import SitemapPagination from "@/components/Common/SitemapPagination";
import {
  PRODUCT_SECTIONS,
  SITEMAP_SECTIONS,
  getProductSectionPage,
  getRangeLabel,
  getSectionConfig,
  getSectionCounts,
  getSitemapData,
  paginate,
  parsePageParam,
} from "@/utils/sitemapData";
import styles from "../Sitemap.module.scss";

const APP_URL = process.env.NEXT_PUBLIC_APP_URL;

const SitemapSection = ({
  sectionKey,
  heading,
  label,
  title,
  description,
  items,
  currentPage,
  totalPages,
  rangeLabel,
  canonical,
  otherSections,
}) => {
  const basePath = `/sitemap/${sectionKey}`;

  return (
    <>
      <MetaTags
        title={title}
        description={description}
        defaultCanonical={canonical}
        NoIndexMetaKey={false}
      />
      {/*
        Canonical is left to MetaTags via `defaultCanonical`, matching every other
        SEO page on the site (see pages/markets/[region]/[slug].jsx). Critically,
        the value passed is the CURRENT page, never page 1 — canonicalising
        pages 2..N to page 1 would deindex them and destroy the link equity this
        split exists to create.

        ⚠️ Known limitation, inherited and NOT introduced here: MetaTags assigns
        its canonical inside a useEffect, so the SSR HTML carries
        `<link rel="canonical" href="">` and the real URL only appears after
        hydration. Emitting a second canonical from here was tried and rejected:
        next/head does not dedupe across separate <Head> instances, so the page
        shipped a real canonical beside an empty one — a worse signal than the
        empty one alone. Recorded as a follow-up instead.

        Note this makes the self-canonical dependent on JS execution, so
        `getCleanUrl` stripping query strings is not a factor here: these are
        route params (/sitemap/products/2), not ?page=2. That is a large part of
        why the pagination is route-based.
      */}
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: "Home", item: `${APP_URL}/` },
          { position: 2, name: "Sitemap", item: `${APP_URL}/sitemap` },
          { position: 3, name: label, item: canonical },
        ]}
      />

      <main className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            {heading} — Page {currentPage} of {totalPages}
          </h1>
          {rangeLabel && <p className={styles.rangeLabel}>{rangeLabel}</p>}
        </header>

        <SitemapLinkList items={items} columns={4} />

        <SitemapPagination
          currentPage={currentPage}
          totalPages={totalPages}
          basePath={basePath}
        />

        <nav className={styles.crossNav} aria-label="Other sitemap sections">
          <p className={styles.crossNavHeading}>Other sections</p>
          <ul className={styles.crossNavList}>
            <li>
              <Link href="/sitemap" className={styles.crossNavLink}>
                Sitemap home
              </Link>
            </li>
            {otherSections.map((section) => (
              <li key={section.key}>
                <Link
                  href={`/sitemap/${section.key}/1`}
                  className={styles.crossNavLink}
                >
                  {section.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </main>
    </>
  );
};

export default SitemapSection;

export async function getServerSideProps({ params, res }) {
  const config = getSectionConfig(params.section);
  const pageNumber = parsePageParam(params.page);

  // Unknown section, or a non-canonical page form ("01", "1.5", "0", "-1").
  // Rejecting rather than normalising keeps [page] from being an infinite URL
  // space that Googlebot will happily probe.
  if (!config || !pageNumber) return { notFound: true };

  try {
    const data = await getSitemapData();
    const counts = getSectionCounts(data);

    // Product-backed sections (products / tds / msds) are paginated by the
    // BACKEND — one request for this page's 250 records, using its tds/msds
    // filters, instead of pulling all ~950 and slicing in memory. Blogs and
    // insights still come from the cached full fetch, because their endpoints
    // do not expose an equivalent filtered feed.
    let items;
    let totalPages;

    if (PRODUCT_SECTIONS[config.key]) {
      const page = await getProductSectionPage(config.key, pageNumber);
      if (!page || !page.items.length) return { notFound: true };
      items = page.items;
      totalPages = page.totalPages;
    } else {
      const sectionItems = data[config.key];
      // null means no data AND no stale fallback — 404 rather than serve an
      // indexable page with zero links.
      if (!sectionItems || !sectionItems.length) return { notFound: true };
      const sliced = paginate(sectionItems, pageNumber);
      items = sliced.items;
      totalPages = sliced.totalPages;
    }

    if (pageNumber > totalPages) return { notFound: true };

    res.setHeader(
      "Cache-Control",
      "public, s-maxage=900, stale-while-revalidate=86400"
    );

    return {
      props: {
        sectionKey: config.key,
        heading: config.heading,
        label: config.label,
        title: config.title(pageNumber, totalPages),
        description: config.description(pageNumber, totalPages),
        items,
        currentPage: pageNumber,
        totalPages,
        rangeLabel: getRangeLabel(items),
        canonical: `${APP_URL}/sitemap/${config.key}/${pageNumber}`,
        // Only link sections that actually have content — never point a crawler
        // at a sibling page that would 404. Counts cover both the
        // backend-paginated and cached sections.
        otherSections: SITEMAP_SECTIONS.filter(
          (section) =>
            section.key !== config.key && counts[section.key]?.total > 0
        ).map((section) => ({ key: section.key, label: section.label })),
      },
    };
  } catch (error) {
    console.error(
      `[sitemap] section "${params.section}" failed:`,
      error?.message ?? error
    );
    return { notFound: true };
  }
}
